import React from 'react';

const FEATURES = [
  { title: 'Connect', desc: 'Link bank accounts across Nigeria, Ghana, Kenya and South Africa with a drop-in widget', icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101' },
  { title: 'Data', desc: 'Transactions, balances, income analysis and account identity in one API call', icon: 'M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7M10 2v5M7 4h6' },
  { title: 'Identity', desc: 'BVN, NIN, CAC, biometric liveness, document verification and AML screening', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
  { title: 'Intelligence', desc: 'AI credit scoring, fraud detection, spending analysis and transaction categorization', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
  { title: 'Webhooks', desc: 'HMAC-signed event delivery with exponential backoff retries', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
  { title: 'Multi-country', desc: 'One integration covers Nigeria, Ghana, Kenya, South Africa with more coming', icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064' },
];

const PLANS = [
  {
    name: 'Starter', price: 'Free', period: '', cta: 'Get started', highlight: false,
    limits: { accounts: '50', apiCalls: '500/mo', identity: '10 included', intelligence: '5 included' },
    features: ['Connect widget + sandbox', 'Account linking (mock data)', 'Balance + transactions', '10 identity checks included', '5 intelligence queries included', 'Community support'],
    excluded: ['Live bank connections', 'Webhooks', 'Custom branding', 'Priority support'],
    perCall: null,
  },
  {
    name: 'Growth', price: 'N50,000', period: '/mo', cta: 'Start building', highlight: true,
    limits: { accounts: '500', apiCalls: '10,000/mo', identity: '100 included', intelligence: '50 included' },
    features: ['Everything in Starter', 'Live bank connections', 'BVN + NIN verification', 'CAC business lookup', 'Basic credit scoring', 'Webhooks (5 endpoints)', 'Email support (48h)'],
    excluded: ['Biometric liveness', 'Custom branding'],
    perCall: 'Then N500/BVN · N600/NIN · N1,000/KYB · N600/AML',
  },
  {
    name: 'Scale', price: 'N200,000', period: '/mo', cta: 'Start scaling', highlight: false,
    limits: { accounts: '5,000', apiCalls: '100,000/mo', identity: '500 included', intelligence: '200 included' },
    features: ['Everything in Growth', 'Biometric + liveness', 'Full KYC/KYB composite', 'Fraud detection + anomalies', 'AML/sanctions screening', 'Webhooks (unlimited)', 'Custom widget branding', 'Priority support (24h)'],
    excluded: [],
    perCall: 'Then N400/BVN · N500/NIN · N900/KYB · N500/AML',
  },
  {
    name: 'Enterprise', price: 'Custom', period: '', cta: 'Talk to us', highlight: false,
    limits: { accounts: 'Unlimited', apiCalls: 'Unlimited', identity: 'Custom volume', intelligence: 'Custom volume' },
    features: ['Everything in Scale', 'Dedicated account manager', 'Custom SLA (99.99%)', 'Bulk verification discounts', 'Multi-region (Africa + NA)', 'SOC 2 compliance support', 'Custom integrations', 'Phone + Slack support'],
    excluded: [],
    perCall: 'Volume pricing from N300/check',
  },
];

const BANKS = [
  { name: 'GTBank', logo: 'https://nigerialogos.com/logos/gtbank/gtbank.svg' },
  { name: 'Zenith Bank', logo: 'https://nigerialogos.com/logos/zenith_bank/zenith_bank.svg' },
  { name: 'Access Bank', logo: 'https://nigerialogos.com/logos/access_bank/access_bank.svg' },
  { name: 'First Bank', logo: 'https://nigerialogos.com/logos/first_bank/first_bank.svg' },
  { name: 'UBA', logo: 'https://nigerialogos.com/logos/uba/uba.svg' },
  { name: 'Kuda', logo: 'https://nigerialogos.com/logos/kuda/kuda.svg' },
  { name: 'Stanbic IBTC', logo: 'https://nigerialogos.com/logos/stanbic_ibtc/stanbic_ibtc.svg' },
  { name: 'FCMB', logo: 'https://nigerialogos.com/logos/fcmb/fcmb.svg' },
  { name: 'Wema Bank', logo: 'https://nigerialogos.com/logos/wema_bank/wema_bank.svg' },
  { name: 'Sterling Bank', logo: 'https://nigerialogos.com/logos/sterling_bank/sterling_bank.svg' },
];

const COUNTRIES = [
  { name: 'Nigeria', flag: 'NG', status: 'Live' },
  { name: 'Ghana', flag: 'GH', status: 'Live' },
  { name: 'Kenya', flag: 'KE', status: 'Live' },
  { name: 'South Africa', flag: 'ZA', status: 'Live' },
  { name: 'Egypt', flag: 'EG', status: 'Coming soon' },
  { name: 'Tanzania', flag: 'TZ', status: 'Coming soon' },
];

const STATS = [
  { value: '35+', label: 'API endpoints' },
  { value: '4', label: 'Countries' },
  { value: '200+', label: 'Financial institutions' },
  { value: '99.9%', label: 'Uptime SLA' },
];

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
              <span className="text-white text-xs font-bold">AL</span>
            </div>
            <span className="text-lg font-bold">AfriLink</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-600">
            <a href="#features" className="hover:text-black transition">Features</a>
            <a href="#pricing" className="hover:text-black transition">Pricing</a>
            <a href="#coverage" className="hover:text-black transition">Coverage</a>
            <a href="/docs" className="hover:text-black transition">Docs</a>
          </div>
          <div className="flex items-center gap-3">
            <a href="/admin" className="text-sm text-gray-600 hover:text-black transition">Sign in</a>
            <a href="/admin" className="px-4 py-2 bg-black text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition">Get API keys</a>
          </div>
        </div>
      </nav>

      <section className="max-w-6xl mx-auto px-6 pt-20 pb-24">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-medium text-emerald-700 mb-6">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              Now live in 4 African countries
            </div>
            <h1 className="text-5xl md:text-6xl font-bold leading-tight tracking-tight mb-6">
              Open banking<br />infrastructure<br />
              <span style={{ color: '#1D9E75' }}>for Africa</span>
            </h1>
            <p className="text-lg text-gray-500 leading-relaxed mb-8 max-w-lg">
              One API to connect bank accounts, verify identities, and unlock financial intelligence across the continent. Build in minutes, not months.
            </p>
            <div className="flex flex-wrap gap-3 mb-6">
              <a href="/admin" className="px-6 py-3.5 bg-black text-white text-sm font-semibold rounded-2xl hover:bg-gray-800 transition">Start for free</a>
              <a href="/widget" className="px-6 py-3.5 text-sm font-semibold rounded-2xl border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 transition">Try the widget</a>
              <a href="/docs" className="px-6 py-3.5 bg-white text-black text-sm font-semibold rounded-2xl border border-gray-200 hover:bg-gray-50 transition">Read the docs</a>
            </div>
            <p className="text-xs text-gray-400">500 free API calls/month. No credit card required.</p>
          </div>
          <div className="bg-gray-950 rounded-2xl p-6 text-sm font-mono leading-relaxed overflow-x-auto">
            <div className="flex gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-red-500 opacity-70"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500 opacity-70"></div>
              <div className="w-3 h-3 rounded-full bg-green-500 opacity-70"></div>
            </div>
            <pre className="text-gray-300"><span className="text-purple-400">const</span> afrilink = <span className="text-purple-400">new</span> <span className="text-yellow-300">AfriLinkConnect</span>({'{'}
  <span className="text-blue-300">publicKey</span>: <span className="text-green-400">'pk_live_...'</span>,
  <span className="text-blue-300">onSuccess</span>: (data) =&gt; console.log(data),
{'}'});

afrilink.<span className="text-yellow-300">open</span>();
<span className="text-gray-600">// Account linked in 30 seconds</span></pre>
          </div>
        </div>
      </section>

      <section className="border-y border-gray-100 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-4xl font-bold text-black">{s.value}</p>
              <p className="text-sm text-gray-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="features" className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">Everything you need to build fintech in Africa</h2>
          <p className="text-gray-500 max-w-2xl mx-auto">From account linking to credit scoring. Replace months of integration work with a single SDK.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="p-6 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition group">
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-black transition">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600 group-hover:text-white transition"><path d={f.icon} /></svg>
              </div>
              <h3 className="text-base font-semibold mb-2">{f.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-16 text-center">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-6">Connected to major financial institutions</p>
          <div className="flex flex-wrap justify-center gap-4">
            {BANKS.map((b) => (
              <div key={b.name} className="px-5 py-3 bg-white rounded-xl border border-gray-100 flex items-center gap-3">
                <img src={b.logo} alt={b.name} className="h-6 w-auto" style={{ maxWidth: '100px' }} onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='block'; }} />
                <span style={{ display: 'none' }} className="text-sm font-medium text-gray-700">{b.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="coverage" className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Pan-African coverage</h2>
          <p className="text-gray-500">Bank data and identity verification across the continent</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
          {COUNTRIES.map((c) => (
            <div key={c.name} className="flex items-center gap-3 p-4 rounded-2xl border border-gray-100">
              <span className="text-sm font-bold text-gray-400">{c.flag}</span>
              <div>
                <p className="text-sm font-semibold">{c.name}</p>
                <p className={"text-xs " + (c.status === 'Live' ? 'text-emerald-600' : 'text-gray-400')}>{c.status}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="bg-gray-50 border-y border-gray-100 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Simple, transparent pricing</h2>
            <p className="text-gray-500">Start free, scale as you grow. No hidden fees.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {PLANS.map((p) => (
              <div key={p.name} className={"p-6 rounded-2xl flex flex-col " + (p.highlight ? 'bg-black text-white ring-2 ring-black' : 'bg-white border border-gray-200')}>
                {p.highlight && <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400 mb-2">Most popular</span>}
                <p className={"text-sm font-semibold mb-1 " + (p.highlight ? 'text-white' : 'text-gray-900')}>{p.name}</p>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-bold">{p.price}</span>
                  {p.period && <span className={"text-sm " + (p.highlight ? 'text-gray-400' : 'text-gray-500')}>{p.period}</span>}
                </div>
                <div className={"text-xs space-y-1.5 mb-4 pb-4 border-b " + (p.highlight ? 'border-gray-700' : 'border-gray-100')}>
                  {Object.entries(p.limits).map(function(entry) { return (
                    <div key={entry[0]} className="flex justify-between">
                      <span className={p.highlight ? 'text-gray-400' : 'text-gray-500'}>{entry[0] === 'apiCalls' ? 'API calls' : entry[0] === 'accounts' ? 'Linked accounts' : entry[0].charAt(0).toUpperCase() + entry[0].slice(1)}</span>
                      <span className="font-medium">{entry[1]}</span>
                    </div>
                  ); })}
                </div>
                <div className="space-y-2 mb-4 flex-1">
                  {p.features.map(function(f) { return (
                    <div key={f} className="flex items-start gap-2 text-xs">
                      <span className={"mt-0.5 flex-shrink-0 " + (p.highlight ? 'text-emerald-400' : 'text-emerald-500')}>&#10003;</span>
                      <span className={p.highlight ? 'text-gray-300' : 'text-gray-600'}>{f}</span>
                    </div>
                  ); })}
                  {(p.excluded || []).map(function(f) { return (
                    <div key={f} className="flex items-start gap-2 text-xs">
                      <span className={"mt-0.5 flex-shrink-0 " + (p.highlight ? 'text-gray-600' : 'text-gray-300')}>&#10007;</span>
                      <span className={p.highlight ? 'text-gray-600' : 'text-gray-300'}>{f}</span>
                    </div>
                  ); })}
                </div>
                <a href="/admin" className={"block text-center py-2.5 rounded-xl text-sm font-semibold transition mt-auto " + (p.highlight ? 'bg-white text-black hover:bg-gray-100' : 'bg-gray-100 text-black hover:bg-gray-200')}>{p.cta}</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-24 text-center">
        <h2 className="text-4xl font-bold mb-4">Ready to build the future of African fintech?</h2>
        <p className="text-gray-500 mb-8 max-w-lg mx-auto">Join developers using AfriLink to power lending, neobanking, payroll, and financial management across Africa.</p>
        <a href="/admin" className="inline-block px-8 py-4 bg-black text-white text-sm font-semibold rounded-2xl hover:bg-gray-800 transition">Get your API keys</a>
      </section>

      <footer className="border-t border-gray-100 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 bg-black rounded-lg flex items-center justify-center">
                  <span className="text-white text-[10px] font-bold">AL</span>
                </div>
                <span className="font-bold">AfriLink</span>
              </div>
              <p className="text-sm text-gray-500">Open banking infrastructure for Africa</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Product</p>
              <div className="space-y-2">
                <a href="/docs" className="block text-sm text-gray-600 hover:text-black transition">Connect Widget</a>
                <a href="/legal" className="block text-sm text-gray-600 hover:text-black transition">Legal</a>
                <a href="/docs" className="block text-sm text-gray-600 hover:text-black transition">Data API</a>
                <a href="/docs" className="block text-sm text-gray-600 hover:text-black transition">Identity API</a>
                <a href="/docs" className="block text-sm text-gray-600 hover:text-black transition">Intelligence</a>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Developers</p>
              <div className="space-y-2">
                <a href="/docs" className="block text-sm text-gray-600 hover:text-black transition">Documentation</a>
                <a href="/docs" className="block text-sm text-gray-600 hover:text-black transition">API Reference</a>
                <a href="/docs" className="block text-sm text-gray-600 hover:text-black transition">SDKs</a>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Company</p>
              <div className="space-y-2">
                <a href="#" className="block text-sm text-gray-600 hover:text-black transition">About</a>
                <a href="#" className="block text-sm text-gray-600 hover:text-black transition">Blog</a>
                <a href="#" className="block text-sm text-gray-600 hover:text-black transition">Contact</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-400">2026 AfriLink. All rights reserved.</p>
            <div className="flex gap-6 text-xs text-gray-400">
              <a href="#" className="hover:text-gray-600">Privacy</a>
              <a href="#" className="hover:text-gray-600">Terms</a>
              <a href="#" className="hover:text-gray-600">Security</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
